/**
 * External Video Web Component
 * Handles YouTube and Vimeo video initialization with custom thumbnails
 *
 * @fires youtube-player-ready - Dispatched when YouTube player is ready
 * @fires vimeo-player-ready - Dispatched when Vimeo player is ready
 */

import { createDebugger } from '../utils/debug.js';

const debug = createDebugger('external-video', false);

// API loading timeouts
const YOUTUBE_API_TIMEOUT = 30000;
const VIMEO_API_TIMEOUT = 15000;

// Singleton API loaders
let youtubeAPIPromise = null;
let vimeoAPIPromise = null;

/**
 * Load YouTube IFrame API (singleton)
 */
const loadYouTubeAPI = () => {
    if (youtubeAPIPromise) return youtubeAPIPromise;

    youtubeAPIPromise = new Promise((resolve, reject) => {
        if (window.YT?.Player) {
            resolve();
            return;
        }

        const existingScript = document.querySelector('script[src*="youtube.com/iframe_api"]');
        if (existingScript) {
            const checkInterval = setInterval(() => {
                if (window.YT?.Player) {
                    clearInterval(checkInterval);
                    resolve();
                }
            }, 100);

            setTimeout(() => {
                clearInterval(checkInterval);
                window.YT?.Player ? resolve() : reject(new Error('YouTube API timeout'));
            }, YOUTUBE_API_TIMEOUT);
            return;
        }

        const tag = document.createElement('script');
        tag.src = 'https://www.youtube.com/iframe_api';
        document.head.appendChild(tag);

        if (!window.onYouTubeIframeAPIReady) {
            window.onYouTubeIframeAPIReady = () => resolve();
        }

        setTimeout(() => {
            if (!window.YT?.Player) reject(new Error('YouTube API failed'));
        }, YOUTUBE_API_TIMEOUT);
    });

    return youtubeAPIPromise;
};

/**
 * Load Vimeo Player API (singleton)
 */
const loadVimeoAPI = () => {
    if (vimeoAPIPromise) return vimeoAPIPromise;

    vimeoAPIPromise = new Promise((resolve, reject) => {
        if (window.Vimeo?.Player) {
            resolve();
            return;
        }

        const existingScript = document.querySelector('script[src*="player.vimeo.com"]');
        if (existingScript) {
            const checkInterval = setInterval(() => {
                if (window.Vimeo?.Player) {
                    clearInterval(checkInterval);
                    resolve();
                }
            }, 100);

            setTimeout(() => {
                clearInterval(checkInterval);
                window.Vimeo?.Player ? resolve() : reject(new Error('Vimeo API timeout'));
            }, VIMEO_API_TIMEOUT);
            return;
        }

        const script = document.createElement('script');
        script.src = 'https://player.vimeo.com/api/player.js';
        script.onload = () => resolve();
        script.onerror = () => reject(new Error('Vimeo API failed'));
        document.head.appendChild(script);

        setTimeout(() => {
            if (!window.Vimeo?.Player) reject(new Error('Vimeo API failed'));
        }, VIMEO_API_TIMEOUT);
    });

    return vimeoAPIPromise;
};

/**
 * Validate video ID format
 */
const validateVideoId = (id, provider) => {
    if (!id) return false;
    if (provider === 'youtube') return /^[a-zA-Z0-9_-]{11}$/.test(id);
    if (provider === 'vimeo') return /^\d+$/.test(id);
    return false;
};

/**
 * External Video Custom Element
 */
class ExternalVideo extends HTMLElement {
    constructor() {
        super();
        this.player = null;
        this.playHandler = null;
    }

    connectedCallback() {
        // Use Intersection Observer for lazy loading
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        this.init();
                        observer.unobserve(this);
                    }
                });
            },
            { rootMargin: '50px' }
        );

        observer.observe(this);
    }

    disconnectedCallback() {
        this.cleanup();
    }

    async init() {
        const container = this.querySelector('[data-video-external-target]');
        if (!container) return;

        const { videoProvider, videoId, loop } = container.dataset;

        if (!videoProvider || !videoId) {
            debug.error('Missing provider or ID');
            return;
        }

        if (!validateVideoId(videoId, videoProvider)) {
            debug.error('Invalid video ID:', videoId);
            this.showError(videoProvider, videoId);
            return;
        }

        try {
            if (videoProvider === 'youtube') {
                await this.initYouTube(container, videoId, loop === 'true');
            } else if (videoProvider === 'vimeo') {
                await this.initVimeo(container, videoId, loop === 'true');
            }
        } catch (error) {
            debug.error('Init failed:', error);
            this.showError(videoProvider, videoId);
        }
    }

    async initYouTube(container, videoId, loop) {
        await loadYouTubeAPI();

        const coverImage = this.querySelector('[data-video-cover-image]');
        const playTrigger = this.querySelector('[data-video-play-trigger]');

        this.playHandler = (e) => {
            e.preventDefault();
            this.player?.playVideo();
        };

        if (playTrigger) {
            playTrigger.addEventListener('click', this.playHandler);
        }

        this.player = new window.YT.Player(container, {
            videoId,
            host: 'https://www.youtube.com',
            playerVars: {
                controls: 1,
                fs: 1,
                modestbranding: 1,
                playsinline: 1,
                rel: 0,
                loop: loop ? 1 : 0,
                playlist: loop ? videoId : undefined,
                origin: window.location.origin
            },
            events: {
                onReady: () => {
                    debug.log('YouTube ready:', videoId);
                    window.dispatchEvent(
                        new CustomEvent('youtube-player-ready', {
                            detail: { element: container, player: this.player }
                        })
                    );
                },
                onStateChange: (event) => {
                    // State 1 = playing
                    if (event.data === 1 && coverImage) {
                        coverImage.style.display = 'none';
                    }
                },
                onError: (error) => {
                    debug.error('YouTube error:', error.data);
                    this.showError('youtube', videoId);
                }
            }
        });
    }

    async initVimeo(container, videoId, loop) {
        await loadVimeoAPI();

        const coverImage = this.querySelector('[data-video-cover-image]');
        const playTrigger = this.querySelector('[data-video-play-trigger]');

        this.player = new window.Vimeo.Player(container, {
            id: videoId,
            loop,
            autoplay: false
        });

        await this.player.ready();
        debug.log('Vimeo ready:', videoId);

        window.dispatchEvent(
            new CustomEvent('vimeo-player-ready', {
                detail: { element: container, player: this.player }
            })
        );

        this.playHandler = (e) => {
            e.preventDefault();
            this.player.play();
        };

        if (playTrigger) {
            playTrigger.addEventListener('click', this.playHandler);
        }

        this.player.on('play', () => {
            if (coverImage) coverImage.style.display = 'none';
        });
    }

    showError(provider, videoId) {
        const videoUrl =
            provider === 'youtube'
                ? `https://www.youtube.com/watch?v=${videoId}`
                : `https://vimeo.com/${videoId}`;

        const error = document.createElement('div');
        error.className =
            'absolute inset-0 flex items-center justify-center bg-black/10 p-4 text-center z-20';
        error.setAttribute('role', 'alert');
        error.innerHTML = `
      <div class="text-text">
        <p class="mb-2">Unable to load video</p>
        <a href="${videoUrl}" target="_blank" rel="noopener noreferrer" class="btn btn--secondary btn--small">
          Watch on ${provider === 'youtube' ? 'YouTube' : 'Vimeo'}
        </a>
      </div>
    `;
        this.appendChild(error);
    }

    cleanup() {
        const playTrigger = this.querySelector('[data-video-play-trigger]');
        if (playTrigger && this.playHandler) {
            playTrigger.removeEventListener('click', this.playHandler);
        }

        if (this.player?.destroy) {
            this.player.destroy();
        }

        this.player = null;
        this.playHandler = null;
    }
}

// Register custom element
if (!customElements.get('external-video')) {
    customElements.define('external-video', ExternalVideo);
}

export { ExternalVideo };
